import React, { Component } from 'react';
import { ReactEditor, Editable, Slate, withReact } from 'nifty-slate-react';
import { Editor, Range, Transforms } from 'slate';
import { Emoji } from 'emoji-mart';
import escape from 'lodash.escape';

import { logError, debugLog as log } from 'shared-services/logger-service';

import { matchHotKey } from '../utils/doc-hotkeys';
import { escapeRegExp, setEndOfContenteditable } from '../utils/helpers';
import { isPopulated, serialize, deserialize, toggleMd, getInputAfterTrigger, niftyEditor, NODE_EMOJI, NODE_MENTION } from '../utils/editor';
import * as userStorage from '../services/user-storage-service';
import { emojisRegex, emoticonsRegex, getColonsByEmoticon } from '../md/plugins/emoji';

import MentionSuggestions from './mention-suggestions';
import MentionUser from './mention-user';
import EditorHoverMenu from './editor-hover-menu';

const MENTION_TRIGGER_CHAR = '@';
const MENTION_TRIGGER_REGEX = /@(\S*)$/;

const defaultValue = [
    {
        type: 'paragraph',
        children: [{ text: '' }]
    }
];

export default class InputTextEditor extends Component {
    static defaultProps = {
        users: [],
        placeholder: 'Post a message…',
        enterToSubmit: true,
        allowEmptySubmit: false
    };

    userMentionSearchTerm = null;

    editor = niftyEditor();

    state = {
        value: this.createValue(this.props.value),
        placeholderDisabled: false
    };

    suggestionsOpen = false;

    handleFocus = () => {
        this.props.onFocus && this.props.onFocus();
    }

    autoReplace(event) {
        if (![186, 32].includes(event.which)) {
            return false;
        }

        const { selection } = this.editor;

        if (!selection || !Range.isCollapsed(selection)) {
            return false
        }

        const [start] = Range.edges(selection);
        const wordBefore = Editor.before(this.editor, start, { unit: 'word' });
        const lineBefore = Editor.before(this.editor, start, { unit: 'line' });
        const before = wordBefore && Editor.before(this.editor, wordBefore);
        const atBeginning = before && before.offset === 0;

        if (!atBeginning && before) {
            before.offset -= 1;
        }

        const beforeRange = before && Editor.range(this.editor, before, start);
        const beforeText = beforeRange && Editor.string(this.editor, beforeRange);

        if (beforeText && !atBeginning && beforeText[0] !== ' ') {
            return;
        }

        const emojiColonsBeforeMatch = beforeText && `${beforeText}:`.match(new RegExp(emojisRegex));

        if (emojiColonsBeforeMatch) {
            const colons = `:${emojiColonsBeforeMatch[1]}:`;

            this.deleteText(colons.length - 1, true);
            this.insertEmoji(colons);
            return true;
        }

        const emojiShortcutBeforeMatch = beforeText && beforeText.match(new RegExp(`${emoticonsRegex}$`));

        if (emojiShortcutBeforeMatch) {
            const shortcut = emojiShortcutBeforeMatch[1];
            const colons = getColonsByEmoticon(shortcut);
            this.deleteText(shortcut.length, true);
            this.insertEmoji(`:${colons}:`);
            return true;
        }

        return false;
    }

    handleBlur = () => {
        this.props.onBlur && this.props.onBlur(this.getSerializedText());

        this.updatePlaceholderState();
    }

    handleChange = value => {
        this.userMentionSearchTerm = getInputAfterTrigger(this.editor, MENTION_TRIGGER_CHAR, MENTION_TRIGGER_REGEX);
        this.setValue(value);

        this.updatePlaceholderState();

        this.props.onChange && this.props.onChange();
    }

    updatePlaceholderState() {
        const placeholderDisabled = Boolean(
            this.editorElement &&
            (
                !(this.editorElement.textContent.trim() === '' && this.editorElement.textContent.indexOf(' ') === -1) ||
                this.editorElement.querySelector('.emoji-mart-emoji')
            )
        );

        if (placeholderDisabled !== this.state.placeholderDisabled) {
            this.setState({ placeholderDisabled });
        }
    }

    handleReturn(event) {
        if (this.suggestionsOpen) {
            event.preventDefault();
            return;
        }

        if (!this.props.enterToSubmit || event.shiftKey) {
            event.preventDefault();
            this.addRow();
            return;
        }

        if (!this.populated && !this.props.allowEmptySubmit) {
            event.preventDefault();
            return;
        }

        event.preventDefault();

        this.submit();
    }

    handleUp(event) {
        if (this.suggestionsOpen) {
            return;
        }

        if (this.populated) {
            return;
        }

        this.props.onUpPress && this.props.onUpPress(event);
    }

    handleSuggestionsShow = () => {
        this.suggestionsOpen = true;
    }

    handleSuggestionsHide = () => {
        this.suggestionsOpen = false;
    }

    handleMentionSelect = userId => {
        this.insertMention(userId, 'user', true);

        this.userMentionSearchTerm = null;
    }

    handleKeyDown = event => {
        requestAnimationFrame(() => {
            this.updatePlaceholderState();
        });

        this.props.onKeyDown && this.props.onKeyDown(event);

        const autoReplace = this.autoReplace(event);

        if (autoReplace) {
            event.preventDefault();

            return;
        }

        const hotKey = matchHotKey(event);

        switch (hotKey.action) {
            case 'undo':
                event.preventDefault();
                this.editor.undo();
                return;

            case 'redo':
                event.preventDefault();
                this.editor.redo();
                return;

            default:
                // eslint
        }

        if (this.toggleAction(hotKey.type)) {
            return;
        }

        switch (event.which) {
            case 27: // Esc
                this.props.onEsc && this.props.onEsc();
                break;

            case 13: // Enter
                this.handleReturn(event);
                return;

            case 38: // Up
                this.handleUp(event);
                return;

            default:
                // eslint
        }
    }

    handlePaste = event => {
        setTimeout(() => {
            requestAnimationFrame(() => {
                this.updatePlaceholderState();
            });
        }, 10);

        if (!this.props.onFilesPaste) {
            return;
        }

        const { nativeEvent } = event;

        event.persist();

        const clipboardData = nativeEvent.clipboardData || nativeEvent.originalEvent.clipboardData;

        if (clipboardData.items && !clipboardData.getData('text/plain')) {
            try {
                const items = Array.from(clipboardData.items);
                const files = items.filter(({ kind }) => kind === 'file').map(item => item.getAsFile()).filter(Boolean);

                if (files.length) {
                    this.props.onFilesPaste(files);
                    event.preventDefault();
                }
            } catch(e) {
                // es lint
            }
        }

    }

    handleMenuClick = action => {
        this.toggleAction(action);
    }

    toggleBold() {
        toggleMd(this.editor, '**',  '**');
    }

    toggleItalic() {
        toggleMd(this.editor, '_', '_');
    }

    toggleCode() {
        toggleMd(this.editor, '`', '`');
    }

    toggleUnorderedList() {
        toggleMd(this.editor, '- ', '', true);
    }

    toggleAction(action) {
        switch (action) {
            case 'bold':
                this.toggleBold();
                return true;

            case 'italic':
                this.toggleItalic();
                return true;

            case 'code':
                this.toggleCode();
                return true;

            case 'unorderedList':
                this.toggleUnorderedList();
                return true;

            default:
                return false;
        }
    }

    addRow() {
        this.insertText('\n');
    }

    createValue(source) {
        if (!source) {
            if (this.props.id) {
                const value = userStorage.get(`editor-${this.props.id}`);

                if (value) {
                    userStorage.set(`editor-${this.props.id}`, null);

                    if ('document' in value) {
                        return defaultValue;
                    }

                    return value;
                }
            }

            return defaultValue;
        }

        // Remove special symbols
        return deserialize(escape(source.split('').join('')));
    }

    getSerializedText() {
        return serialize({
            children: this.state.value
        }).replace(/\n+$/, '');
    }

    submit() {
        this.props.onSubmit && this.props.onSubmit(this.getSerializedText());

        if (this.props.emptyOnSubmit) {
            this.empty();
        }
    }

    empty() {
        Transforms.setSelection(this.editor, {
            anchor: { path: [0, 0], offset: 0 },
            focus: { path: [0, 0], offset: 0 }
        });

        this.setValue(this.createValue());

        this.setState({
            placeholderDisabled: false
        });
    }

    setValue(value) {
        this.setState({ value });
    }

    focus() {
        ReactEditor.focus(this.editor);
        const slateElement = this.editorElement?.children[0];

        if (slateElement) {
            slateElement.focus();
            setEndOfContenteditable(slateElement);
        }
    }

    blur() {
        ReactEditor.blur(this.editor);
    }

    insertText(text) {
        Editor.insertText(this.editor, text);
    }

    deleteText(distance, reverse) {
        Transforms.delete(this.editor, {
            at: this.editor.selection.focus,
            distance,
            reverse,
            unit: 'character'
        });
    }

    insertEmoji(colons) {
        log('[INPUT TEXT EDITOR] Focus from insert emoji');

        this.insertInlineNode({
            type: NODE_EMOJI,
            data: { colons },
            children: [{ text: '' }]
        });
    }

    insertMention(id, type, fromSuggestion) {
        if (fromSuggestion && this.editor.selection) {
            this.deleteText((this.userMentionSearchTerm?.length || 0) + 1, true);
        }

        log('[INPUT TEXT EDITOR] Focus from insert mention');

        this.insertInlineNode({
            type: NODE_MENTION,
            data: { id, type },
            children: [{ text: '' }]
        });
    }

    insertInlineNode(node) {
        Transforms.insertNodes(this.editor, node);

        Transforms.move(this.editor);
        this.insertText(' ');

        setTimeout(() => {
            this.focus();
        }, 100);
    }

    renderElement = props => {
        const { attributes, children, element } = props;

        switch (element.type) {
            case NODE_EMOJI:
                return <span
                    {...attributes}
                    // selected={isFocused}
                    contentEditable={false}
                    className="emoji-holder"
                    onDrop={e => e.preventDefault()}
                >
                    <Emoji emoji={element.data.colons} size={20} />

                    { children }
                </span>

            case NODE_MENTION:
                return <span
                    {...attributes}
                    // selected={isFocused}
                    contentEditable={false}
                    onDrop={e => e.preventDefault()}
                >
                    <MentionUser id={element.data.id} clickable={false} />

                    { children }
                </span>

            default:
                return <p {...attributes}>{children}</p>;
        }
    }

    get populated() {
        return isPopulated(this.editor);
    }

    setEditorElement = element => this.editorElement = element;

    componentDidMount() {
        this.props.provideController && this.props.provideController({
            insertText: text => {
                this.insertText(text);
            },
            insertEmoji: colons => {
                this.insertEmoji(colons);
            },
            focus: () => {
                log('[INPUT TEXT EDITOR] Focus from controller');
                this.focus();
            },
            blur: () => {
                this.blur();
            },
            empty: () => {
                this.empty();
            },
            isPopulated: () => this.populated,
            getSerializedText: () => this.getSerializedText()
        });

        if (this.props.autofocus) {
            log('[INPUT TEXT EDITOR] Focus from autofocus');

            this.focus();
        }

        this.updatePlaceholderState();
    }

    componentWillUnmount() {
        if (this.props.id) {
            userStorage.set(`editor-${this.props.id}`, this.state.value);
        }

        this.props.provideController && this.props.provideController(null);
    }

    shouldComponentUpdate(nextProps, nextState) {
        if (
            (this.props.enterToSubmit !== nextProps.enterToSubmit) ||
            (this.props.value !== nextProps.value) ||
            (this.props.allowEmptySubmit !== nextProps.allowEmptySubmit) ||
            (this.props.onFocus !== nextProps.onFocus) ||
            (this.props.onBlur !== nextProps.onBlur) ||
            (this.props.onKeyDown !== nextProps.onKeyDown) ||
            (this.props.onEsc !== nextProps.onEsc) ||
            (this.props.onSubmit !== nextProps.onSubmit) ||
            (this.props.emptyOnSubmit !== nextProps.emptyOnSubmit) ||
            (this.props.autofocus !== nextProps.autofocus) ||
            (this.props.provideController !== nextProps.provideController) ||
            (this.props.placeholder !== nextProps.placeholder) ||
            (this.props.style !== nextProps.style) ||
            // (this.props.users !== nextProps.users) ||
            (this.state.value !== nextState.value) ||
            (this.state.placeholderDisabled !== nextState.placeholderDisabled)
        ) {
            return true;
        };

        return false;
    }

    componentDidCatch(error, info) {
        logError('editor', error, { info });
    }

    render() {
        return (
            <div
                className="input-text-editor"
                ref={this.setEditorElement}
                data-placeholder={this.state.placeholderDisabled ? '' : this.props.placeholder} style={this.props.style}
                onPaste={this.handlePaste}
            >
                <Slate editor={this.editor} value={this.state.value} onChange={this.handleChange} key={this.props.id}>
                    <Editable
                        renderElement={this.renderElement}
                        spellCheck={true}
                        autoFocus={this.props.autofocus}
                        onKeyDown={this.handleKeyDown}
                        onFocus={this.handleFocus}
                        onBlur={this.handleBlur}
                    />

                    <EditorHoverMenu onClick={this.handleMenuClick} />
                </Slate>

                <MentionSuggestions
                    users={this.props.users}
                    term={this.userMentionSearchTerm}
                    onSelect={this.handleMentionSelect}
                    onShow={this.handleSuggestionsShow}
                    onHide={this.handleSuggestionsHide}
                />
            </div>
        );
    }
}
